/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Description from "App/model/description";
import {vec3} from "gl-matrix";
import Serializer from "App/model/serializer";
import {RenderElement} from "App/model/elements/element";
import BoundingBox from "App/utils/bounding_box";

class GroupData {
    public sceneAPIModelUID: number;
    public group_id: number = -2;   // actually used to distinguish between:
                                    //   -2   : model group (group representing a whole model)
                                    //   -1   : ungroupped objects
                                    //   >= 0 : group id unique in the model scope
    public name: string = "";
    public visible: number = 2;                                     // 0: not visible 1: partially visible 2: visible
    public hidden: boolean = false;                                  // if true, then do not show the group item in the group tree window
    public selected: boolean = false;                                // pick selection
    public color: string = "";
    public camera: any = {
        type: "",
        position: {
            x: 0,
            y: 0,
            z: 0
        },
        target: {
            x: 0,
            y: 0,
            z: 0
        },
        fov: 0, // FOV is vertical
        roll: 0
    };
    public icon: number = 0;
    public expanded: boolean = false;
    public children: Group[] = [];
    public parent: Group = null;

    constructor(sceneapiModelUID: number) {
        this.sceneAPIModelUID = sceneapiModelUID;
    }
}

export default
class Group {

    public data: GroupData;
    private _modelDescription: Description;
    private _elements: any = {};

    constructor(aModelDescription: Description) {

        this._modelDescription = aModelDescription !== undefined ? aModelDescription : null;
        this.data = new GroupData(aModelDescription ? this._modelDescription.getRenderModel().getSceneAPIModelUID() : -1);
    }

    public serialize(): any {
        let shallowCopy = Serializer.shallowClone(this.data);

        let cameraPosition = vec3.fromValues(shallowCopy.camera.position.x, shallowCopy.camera.position.y, shallowCopy.camera.position.z);
        let cameraTarget = vec3.fromValues(shallowCopy.camera.target.x, shallowCopy.camera.target.y, shallowCopy.camera.target.z);

        shallowCopy.camera.position.x = cameraPosition[0];
        shallowCopy.camera.position.y = cameraPosition[1];
        shallowCopy.camera.position.z = cameraPosition[2];

        shallowCopy.camera.target.x = cameraTarget[0];
        shallowCopy.camera.target.y = cameraTarget[1];
        shallowCopy.camera.target.z = cameraTarget[2];

        shallowCopy.children = [];
        for (let i in this.data.children) {
            shallowCopy.children[i] = this.data.children[i].serialize();
            shallowCopy.children[i].parent = shallowCopy;
        }

        return shallowCopy;
    }

    public getID(): number {
        return this.data.group_id;
    }

    public getModelID(): number {
        return this.data.sceneAPIModelUID;
    }

    public getColorRGBf(): number[] {
        let hex = this.data.color.replace(/[^0-9A-F]/gi, '');

        let hexToRgbf = function(hex) {
            let bigint = parseInt(hex, 16);
            let r = (bigint >> 16) & 255;
            let g = (bigint >> 8) & 255;
            let b = bigint & 255;

            return [r / 255.0, g / 255.0, b / 255.0];
        };

        return hexToRgbf(hex);
    }

    public getName(): string {
        return this.data.name;
    }

    public getColor(): string {
        return this.data.color;
    }

    public getSelected(): boolean {
        return this.data.selected;
    }

    public setSelected(aValue: boolean) {
        this.data.selected = aValue;
    }

    public getParent(): Group {
        return this.data.parent;
    }

    public addChild(aChildGroup: Group) {
        aChildGroup.data.parent = this;
        this.data.children.push(aChildGroup);
    }

    public removeChild(aChildGroup: Group) {
        aChildGroup.data.parent = null;
        for (let i = 0; i < this.data.children.length; ++i) {
            if (this.data.children[i] === aChildGroup) {
                delete this.data.children[i];
            }
        }
    }

    public updateParentVisibility() {
        let allChildrenVisible = true;
        let allChildrenHidden = true;

        for (let i in this.data.children) {
            let child = this.data.children[i];
            allChildrenVisible = allChildrenVisible && child.data.visible === 2;
            allChildrenHidden = allChildrenHidden && child.data.visible === 0;
        }

        if (allChildrenVisible) {
            this.data.visible = this.data.visible === 0 ? 1 : 2;
        } else if (allChildrenHidden) {
            this.data.visible = this.data.visible === 2 ? 1 : 0;
        } else {
            this.data.visible = 1;
        }

        if (this.data.parent) {
            this.data.parent.updateParentVisibility();
        }
    }

    /***
     * Set visibility of the group tree and appropriate elements and children
     * @param aValue
     */
    public setVisibility(aValue: number) {
        this.data.visible = aValue > 0 ? 2 : 0; // the group visibility can be visible (2) or not visible (0),
                                                  // the intermediate state (1) can be reached only be the getVisibility getter
        for (let key in this._elements) {
            this._elements[key].setVisibility(this.data.visible > 0);
        }

        // update children visibility
        for (let i in this.data.children) {
            let child = this.data.children[i];
            child.setVisibility(this.data.visible);
        }
    }

    public getVisibility(): number {
        return this.data.visible;
    }

    public addElement(aRenderElement: RenderElement) {
        aRenderElement.setGroup(this);
        aRenderElement.setVisibility(this.data.visible > 0);
        this._elements[aRenderElement.getUID()] = aRenderElement;
    }

    public removeElement(aRenderElement: RenderElement) {
        // look for the removed element in current group
        let element = this._elements[aRenderElement.getUID()];
        if (element !== undefined) {
            // remove the element from the children
            delete this._elements[aRenderElement.getUID()];
        }
    }

    public getElements(): any {
        return this._elements;
    }

    public getBoundingBoxFromAllVisibleObjects(): BoundingBox {
        let bbox = new BoundingBox();
        let elements = [];
        this.getAllVisibleElements(elements);
        for (let i = 0; i < elements.length; i++) {
            elements[i].getBoundingBox(bbox);
        }

        return bbox;
    }

    public getAllVisibleElements(aRenderElements: any) {
        for (let key in this._elements) {
            let element = this._elements[key];
            if (element.getVisibility()) {
                aRenderElements.push(element);
            }
        }

        for (let i in this.data.children) {
            let child = this.data.children[i];
            child.getAllElements(aRenderElements);
        }
    }

    public getAllElements(aRenderElements: any) {
        for (let key in this._elements) {
            let element = this._elements[key];
            aRenderElements.push(element);
        }

        for (let i in this.data.children) {
            let child = this.data.children[i];
            child.getAllElements(aRenderElements);
        }
    }

    public parse(aGroupJSON: any): boolean {
        if (!aGroupJSON.hasOwnProperty("group_id")) {
            return false;
        }
        if (!aGroupJSON.hasOwnProperty("name")) {
            return false;
        }

        this.data.group_id = aGroupJSON.group_id;
        this.data.name = aGroupJSON.name;

        // if the visible attribute is not defined, then the layer is marked as visible
        if (aGroupJSON.hasOwnProperty("visible")) {
            this.data.visible = aGroupJSON.visible;
        }

        if (aGroupJSON.hasOwnProperty("color")) {
            this.data.color = aGroupJSON.color;
        }

        if (aGroupJSON.hasOwnProperty("icon")) {
            this.data.icon = aGroupJSON.icon;
        }

        if (aGroupJSON.hasOwnProperty("camera_type")) {
            this.data.camera.type = aGroupJSON.camera_type;
        }

        if (aGroupJSON.hasOwnProperty("camera_position")) {
            this.data.camera.position.x = aGroupJSON.camera_position[0];
            this.data.camera.position.y = aGroupJSON.camera_position[1];
            this.data.camera.position.z = aGroupJSON.camera_position[2];
        }

        if (aGroupJSON.hasOwnProperty("camera_target")) {
            this.data.camera.target.x = aGroupJSON.camera_target[0];
            this.data.camera.target.y = aGroupJSON.camera_target[1];
            this.data.camera.target.z = aGroupJSON.camera_target[2];
        }

        if (aGroupJSON.hasOwnProperty("camera_fov")) {
            this.data.camera.fov = aGroupJSON.camera_fov;
        }

        if (aGroupJSON.hasOwnProperty("camera_roll")) {
            this.data.camera.roll = aGroupJSON.camera_roll;
        }

        if (aGroupJSON.hasOwnProperty("expanded")) {
            this.data.expanded = aGroupJSON.expanded;
        }

        if (aGroupJSON.hasOwnProperty("hidden")) {
            this.data.hidden = aGroupJSON.hidden;
        }

        this.parseElements(aGroupJSON);

        if (aGroupJSON.hasOwnProperty("children")) {
            for (let i in aGroupJSON.children) {
                this._modelDescription.groupTree.addGroup(this, aGroupJSON.children[i]);
            }
        }

        return true;
    }

    public parseElements(aGroupJSON: any): boolean {
        if (!aGroupJSON.hasOwnProperty("elements")) {
            return true;
        }

        let elements = this._modelDescription.getElements();
        let element = null;
        for (let i = 0; i < aGroupJSON.elements.length; i++) {
            element = elements.getElementByParsedUID(aGroupJSON.elements[i]);
            if (element) {
                element.setGroup(this);

                element.setVisibility(this.data.visible > 0);

                this._elements[element.getUID()] = element;
            }
        }

        return true;
    }

    public getJSONRepresentation(): any {
        let elements = [];

        for (let key in this._elements) {
            let element = this._elements[key];
            elements.push(element.getParsedUID());
        }

        let res = {
            group_id: this.data.group_id,
            name: this.data.name,
            visible: this.data.visible,
            hidden: this.data.hidden,
            color: this.data.color,
            camera: {
                type: this.data.camera.type,
                position: {
                    x: this.data.camera.position.x,
                    y: this.data.camera.position.y,
                    z: this.data.camera.position.z
                },
                target: {
                    x: this.data.camera.target.x,
                    y: this.data.camera.target.y,
                    z: this.data.camera.target.z
                },
                fov: this.data.camera.fov,
                roll: this.data.camera.roll
            },    // FOV is vertical
            icon: this.data.icon,
            expanded: this.data.expanded,
            elements: elements,
            children: []
        };

        for (let i = 0; i < this.data.children.length; i++) {
            res.children.push(this.data.children[i].getJSONRepresentation());
        }

        return res;
    }
}
